/**
 * Wave 40 (FINAL): Remaining capitals + major cities
 * Vientiane, Ulaanbaatar, N'Djamena, Port Moresby, Douala
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development.' },
  { name: 'Web Application Development', description: 'Enterprise web applications and portals.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions and automation.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure and CI/CD.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping.' },
  { name: 'Cybersecurity', description: 'Security audits, compliance, penetration testing.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers integrated into your team.' },
];
const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Mobile money, payment platforms.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplaces.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling.' },
  { name: 'AgriTech', description: 'Agricultural technology platforms.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, optimization.' },
];
const makeFaqs = (city: string, country: string) => [
  { question: `Why ${city}?`, answer: `${city}, ${country} offers strategic positioning and competitive rates.` },
  { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
  { question: 'Quality?', answer: 'Code reviews, CI/CD, 80%+ test coverage.' },
  { question: 'Compliance?', answer: 'ISO 27001, GDPR-aligned, NDAs.' },
  { question: 'Start time?', answer: '2-week onboarding.' },
  { question: 'Models?', answer: 'T&M, fixed-price, dedicated teams.' },
];

export const CITY_AGENCY_PAGES_WAVE40: Record<string, CityAgencyData> = {
  vientiane: {
    slug: 'software-development-agency-vientiane', cityName: 'Vientiane', countryName: 'Laos', flag: '🇱🇦',
    timezone: 'ICT (UTC+7)', currency: 'LAK',
    title: { en: 'Software Development Agency Vientiane | Call IT Dev', fr: 'Agence Développement Informatique Vientiane | Call IT Dev' },
    description: { en: 'Software development for Vientiane, Laos. ASEAN member, emerging digital economy.', fr: 'Agence de développement pour Vientiane, Laos. Membre ASEAN, économie numérique émergente.' },
    keywords: ['software development Vientiane', 'IT outsourcing Laos'],
    heroTitle: { en: 'Software Development Agency for Vientiane', fr: 'Agence de Développement pour Vientiane' },
    heroSubtitle: { en: 'Laos capital — ASEAN member with emerging tech sector and competitive rates.', fr: 'Capitale du Laos — membre ASEAN avec secteur tech émergent.' },
    whyTitle: { en: 'Why Vientiane', fr: 'Pourquoi Vientiane' },
    whyReasons: [
      { icon: '🌏', title: 'ASEAN Member', description: 'Access to 700M+ ASEAN market.' },
      { icon: '💰', title: '65% Savings', description: 'Ultra-competitive rates.' },
      { icon: '📈', title: 'Digital Growth', description: 'Laos digital economy growing rapidly.' },
      { icon: '⏰', title: 'ICT Timezone', description: 'Asian timezone for APAC clients.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Vientiane', 'Laos'),
    localRate: '$15-30', ourRate: '$20-35', savings: '50%+',
  },
  ulaanbaatar: {
    slug: 'software-development-agency-ulaanbaatar', cityName: 'Ulaanbaatar', countryName: 'Mongolia', flag: '🇲🇳',
    timezone: 'ULAT (UTC+8)', currency: 'MNT',
    title: { en: 'Software Development Agency Ulaanbaatar | Call IT Dev', fr: 'Agence Développement Informatique Oulan-Bator | Call IT Dev' },
    description: { en: 'Software development for Ulaanbaatar, Mongolia. Growing tech scene, strategic China/Russia position.', fr: 'Agence de développement pour Oulan-Bator, Mongolie.' },
    keywords: ['software development Ulaanbaatar', 'IT outsourcing Mongolia'],
    heroTitle: { en: 'Software Development Agency for Ulaanbaatar', fr: 'Agence de Développement pour Oulan-Bator' },
    heroSubtitle: { en: 'Mongolia\'s capital bridging China and Russia with emerging tech talent.', fr: 'Capitale de Mongolie entre Chine et Russie avec talents tech émergents.' },
    whyTitle: { en: 'Why Ulaanbaatar', fr: 'Pourquoi Oulan-Bator' },
    whyReasons: [
      { icon: '🌍', title: 'Strategic Position', description: 'Between China and Russia — access to both markets.' },
      { icon: '💰', title: '60% Savings', description: 'Competitive rates.' },
      { icon: '🎓', title: 'Tech Talent', description: 'Young population with growing IT education.' },
      { icon: '⛏️', title: 'Mining Economy', description: 'Mining drives tech modernization demand.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Ulaanbaatar', 'Mongolia'),
    localRate: '$20-40', ourRate: '$20-35', savings: '50%+',
  },
  ndjamena: {
    slug: 'software-development-agency-ndjamena', cityName: "N'Djamena", countryName: 'Chad', flag: '🇹🇩',
    timezone: 'WAT (UTC+1)', currency: 'XAF',
    title: { en: "Software Development Agency N'Djamena | Call IT Dev", fr: "Agence Développement Informatique N'Djamena | Call IT Dev" },
    description: { en: "Software development for N'Djamena, Chad. French/Arabic bilingual, Sahel hub.", fr: "Agence de développement pour N'Djamena, Tchad. Bilingue français/arabe." },
    keywords: ["software development N'Djamena", 'IT outsourcing Chad'],
    heroTitle: { en: "Software Development Agency for N'Djamena", fr: "Agence de Développement pour N'Djamena" },
    heroSubtitle: { en: "Chad's capital — bilingual French/Arabic with oil-driven digital modernization.", fr: "Capitale du Tchad — bilingue français/arabe avec modernisation numérique." },
    whyTitle: { en: "Why N'Djamena", fr: "Pourquoi N'Djamena" },
    whyReasons: [
      { icon: '🗣️', title: 'Bilingual', description: 'French and Arabic speaking teams.' },
      { icon: '💰', title: '70% Savings', description: 'Ultra-competitive rates.' },
      { icon: '🛢️', title: 'Oil Economy', description: 'Oil sector drives digital demand.' },
      { icon: '⏰', title: 'WAT = CET', description: 'European timezone alignment.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs("N'Djamena", 'Chad'),
    localRate: '$15-28', ourRate: '$20-35', savings: '50%+',
  },
  portMoresby: {
    slug: 'software-development-agency-port-moresby', cityName: 'Port Moresby', countryName: 'Papua New Guinea', flag: '🇵🇬',
    timezone: 'PGT (UTC+10)', currency: 'PGK',
    title: { en: 'Software Development Agency Port Moresby | Call IT Dev', fr: 'Agence Développement Informatique Port Moresby | Call IT Dev' },
    description: { en: 'Software development for Port Moresby, PNG. Pacific hub, English-speaking.', fr: 'Agence de développement pour Port Moresby, Papouasie-Nouvelle-Guinée.' },
    keywords: ['software development Port Moresby', 'IT outsourcing PNG'],
    heroTitle: { en: 'Software Development Agency for Port Moresby', fr: 'Agence de Développement pour Port Moresby' },
    heroSubtitle: { en: 'PNG\'s capital — Pacific resource hub with English-speaking business environment.', fr: 'Capitale de PNG — hub de ressources du Pacifique anglophone.' },
    whyTitle: { en: 'Why Port Moresby', fr: 'Pourquoi Port Moresby' },
    whyReasons: [
      { icon: '🗣️', title: 'English Speaking', description: 'English is an official language.' },
      { icon: '⛏️', title: 'Resource Economy', description: 'Mining and LNG drive digital modernization.' },
      { icon: '💰', title: '55% Savings', description: 'Competitive rates.' },
      { icon: '🌊', title: 'Pacific Position', description: 'Strategic Melanesian position.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Port Moresby', 'Papua New Guinea'),
    localRate: '$30-50', ourRate: '$20-35', savings: '50%+',
  },
  douala: {
    slug: 'software-development-agency-douala', cityName: 'Douala', countryName: 'Cameroon', flag: '🇨🇲',
    timezone: 'WAT (UTC+1)', currency: 'XAF',
    title: { en: 'Software Development Agency Douala | Call IT Dev', fr: 'Agence Développement Informatique Douala | Call IT Dev' },
    description: { en: 'Software development for Douala, Cameroon. Economic capital, bilingual French/English.', fr: 'Agence de développement pour Douala, Cameroun. Capitale économique, bilingue.' },
    keywords: ['software development Douala', 'IT outsourcing Cameroon', 'tech development Douala'],
    heroTitle: { en: 'Software Development Agency for Douala', fr: 'Agence de Développement pour Douala' },
    heroSubtitle: { en: 'Cameroon\'s economic capital — bilingual French/English port city with tech ambitions.', fr: 'Capitale économique du Cameroun — ville portuaire bilingue avec ambitions tech.' },
    whyTitle: { en: 'Why Douala', fr: 'Pourquoi Douala' },
    whyReasons: [
      { icon: '🏙️', title: 'Economic Capital', description: 'Douala is Cameroon\'s business and economic hub.' },
      { icon: '🗣️', title: 'Bilingual', description: 'French and English speaking teams.' },
      { icon: '💰', title: '65% Savings', description: 'Competitive nearshore rates.' },
      { icon: '🚢', title: 'Port City', description: 'Central Africa\'s largest port — logistics tech hub.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Douala', 'Cameroon'),
    localRate: '$20-40', ourRate: '$20-35', savings: '55%+',
  },
};
